import { Divider, IconButton, Menu, MenuItem, Tooltip } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import React, { useMemo } from 'react';
import { styles } from './FolderActionsMenuButton.styles';
import { useFoldersContext } from '../../contexts/useFolders.context';
import { getMaxFoldersDepth } from './FoldersList.utils';
import { FOLDER_DEPTH_LIMIT } from '../LocationSelect/hooks/useIsUnderDepthLimit';

const intl = {
  folderActions: 'folder actions',
  move: 'Move',
  cannotMoveFolderAtDepthLimit: (depthLimit: number) =>
    `You cannot move a folder with subfolders that is at the depth limit (of ${depthLimit})`,
  duplicate: 'Duplicate',
  rename: 'Rename',
  delete: 'Delete'
};

export function FolderActionsMenuButton() {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const {
    activeFolder,
    folderAbilities,
    setIsDuplicateFolderDialogOpen,
    setIsMoveFolderDialogOpen,
    setIsRenameFolderDialogOpen,
    setIsDeleteFolderDialogOpen
  } = useFoldersContext();
  const isUnderDepthLimit = useMemo(() => {
    const maxFoldersDepth = getMaxFoldersDepth(activeFolder.subfolders || []);
    // "+ 1" because the activeFolder is also included in the depth count
    return maxFoldersDepth + 1 < FOLDER_DEPTH_LIMIT;
  }, [activeFolder.subfolders]);

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <IconButton
        edge="end"
        id="folder-actions-button"
        aria-label={intl.folderActions}
        aria-controls={open ? 'folder-actions-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
        onClick={(event) => {
          event.stopPropagation();
          setAnchorEl(event.currentTarget);
        }}
      >
        <MoreVertIcon sx={styles.buttonIcon} />
      </IconButton>
      <Menu
        id="folder-actions-menu"
        MenuListProps={{ 'aria-labelledby': 'folder-actions-button' }}
        anchorEl={anchorEl}
        open={open}
        onClose={() => {
          handleClose();
        }}
        slotProps={{
          paper: { style: styles.menuPaper }
        }}
      >
        {folderAbilities.move ? (
          <Tooltip
            placement="bottom-start"
            title={!isUnderDepthLimit ? intl.cannotMoveFolderAtDepthLimit(FOLDER_DEPTH_LIMIT) : undefined}
            arrow
          >
            <span>
              <MenuItem
                sx={styles.menuItem}
                disabled={!isUnderDepthLimit}
                onClick={(event) => {
                  event.stopPropagation();
                  handleClose();
                  setIsMoveFolderDialogOpen(true);
                }}
              >
                {intl.move}
              </MenuItem>
            </span>
          </Tooltip>
        ) : null}

        {folderAbilities.duplicate ? (
          <MenuItem
            sx={styles.menuItem}
            onClick={(event) => {
              event.stopPropagation();
              handleClose();
              setIsDuplicateFolderDialogOpen(true);
            }}
          >
            {intl.duplicate}
          </MenuItem>
        ) : null}

        {folderAbilities.rename ? (
          <MenuItem
            sx={styles.menuItem}
            onClick={(event) => {
              event.stopPropagation();
              handleClose();
              setIsRenameFolderDialogOpen(true);
            }}
          >
            {intl.rename}
          </MenuItem>
        ) : null}

        <Divider sx={styles.divider} />
        {folderAbilities.delete ? (
          <MenuItem
            sx={{ ...styles.menuItem, ...styles.deleteMenuItem }}
            onClick={(event) => {
              event.stopPropagation();
              handleClose();
              setIsDeleteFolderDialogOpen(true);
            }}
          >
            {intl.delete}
          </MenuItem>
        ) : null}
      </Menu>
    </>
  );
}
